// @react
import React, { ReactElement, useState, useContext } from 'react'
// @types
import {
  GetUserQueryResult,
  IMembership,
  UserUpdateOrderAutorenewalMutation,
  UserUpdateOrderAutorenewalMutationVariables,
} from 'lib/graphql/graphqlTypes'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
// @common
import { vsprintf } from 'sprintf-js'
import strings from 'lib/constants/strings'
import Helper from 'utils/Helper.class'
// @context
import { SnackbarContext } from 'modules/common/context/snackbar'
// @apollo
import { useMutation } from '@apollo/client'
import { USER_UPDATE_ORDER_AUTORENEWAL } from 'graphql/mutations/user.mutation'
// @components
import Grid from '@bit/atd.web.grid'
import TextLink from '@bit/atd.web.text-link'
import Spacer from '@bit/atd.web.spacer'
import Typography from '@material-ui/core/Typography'
import Modal from '@bit/atd.web.modal'
import InputText from '@bit/atd.web.forms.input-text'
import InputSectionLabel from '@bit/atd.web.forms.input-section-label'
import Button from '@bit/atd.web.button'
import Toggle from '@bit/atd.web.toggle'
import Box from '@bit/atd.web.box'
// @module Components
import AutoRenewForm from 'modules/profile/components/Membership/Form/AutoRenew'
import client from 'graphql/Apollo/apolloClient'
import colors from '@bit/atd.web.colors/dist/colors'

type ChapterMemberRenewalType = {
  isAutoRenewal?: boolean
  orderNumber?: string
}
export type ChapterMemberRenewalArrayType = ChapterMemberRenewalType[]

enum MembershipTypes {
  CURRENT = 'CURRENT',
  FUTURE = 'FUTURE',
  CHAPTER = 'CHAPTER',
}

export type MembershipContext = {
  membership: MembershipTypes
  typeLabel: string
  orderNumber: string
  orderLineNumber: string
  isAutoRenewal: boolean
  membershipObject?: IMembership | null
}

export type AutoRenewalSubmitData = {
  orderNumber: string
  orderLineNumber: string
  isAutoRenewal?: boolean
  creditCardType?: string
  creditCardNumber?: string
  creditCardSecurityCode?: string
  nameOnCard?: string
  expirationYear?: number
  expirationMonth?: number
}

export type AutoRenewToggle = {
  type: MembershipTypes
  membershipContext: Omit<
    MembershipContext,
    'orderLineNumber' | 'typeLabel' | 'membership'
  >
  chapterRenew?: ChapterMemberRenewalArrayType
}

type PropsType = {
  data?: GetUserQueryResult['data']
  refetch: () => void
}

const MembershipForm = ({ data: userData }: PropsType): ReactElement => {
  /******************************
   *  PROPS
   *******************************/
  const currentMembership = userData?.user?.membership.currentMembership
  const futureMembership = userData?.user?.membership.futureMembership
  const chapterMemberships = userData?.user?.membership.chapterMembership

  /******************************
   *  STATE
   *******************************/
  const [
    membershipContext,
    setMembershipContext,
  ] = useState<MembershipContext>()
  const [renewModal, setRenewModal] = useState<boolean>(false)
  const [renewResponseModal, setRenewResponseModal] = useState<boolean>(false)
  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)

  /******************************
   *  GQL HOOKS
   *******************************/
  const [updateUserOrderAutoRenewal] = useMutation<
    UserUpdateOrderAutorenewalMutation,
    UserUpdateOrderAutorenewalMutationVariables
  >(USER_UPDATE_ORDER_AUTORENEWAL)

  /******************************
   *  HOOKS
   *******************************/
  const snackbar = useContext(SnackbarContext)

  /******************************
   *  FUNCTIONS
   *******************************/
  const dateRangeLabelFormatter = (beginDate: number, endDate: number) => {
    return `${Helper.smallDateFormatter(
      beginDate,
    )} - ${Helper.smallDateFormatter(endDate)}`
  }

  /**
   * CC type codes allowed AE,DS,MC,VI
   * @param data
   * @param targetAutorenewal
   * @param context
   */
  const userOrderAutorenewalUpdateHandler = async (
    data: AutoRenewalSubmitData,
    targetAutorenewal: boolean,
    context: MembershipContext,
  ): Promise<boolean> => {
    setIsSubmitting(true)

    const input = targetAutorenewal
      ? {
          orderNumber: data.orderNumber,
          orderLineNumber: data.orderLineNumber,
          isAutoRenewal: true,
          creditCardType: data.creditCardType,
          creditCardNumber: data.creditCardNumber,
          creditCardSecurityCode: data.creditCardSecurityCode,
          nameOnCard: data.nameOnCard,
          expirationYear: data.expirationYear,
          expirationMonth: data.expirationMonth,
        }
      : {
          orderNumber: data.orderNumber,
          orderLineNumber: data.orderLineNumber,
          isAutoRenewal: false,
        }

    const { errors: mutationErrors } = await updateUserOrderAutoRenewal({
      variables: {
        input,
      },
    })
    setIsSubmitting(false)

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
          'Membership Autorenewal',
        ]),
        show: true,
      })

      if (context.membershipObject) {
        client.cache.modify({
          id: client.cache.identify(context.membershipObject),
          fields: {
            autoRenew() {
              return targetAutorenewal
            },
          },
        })
      }
      return true
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: strings.ERRORS.GENERIC_UPDATE_ERROR,
        show: true,
      })
      console.log('mutation error', mutationErrors)
    }
    return false
  }

  // on Auto-Renew Toggle Handlers

  /**
   *
   * @param context
   */
  const onRenewHandler = async (context: MembershipContext) => {
    setMembershipContext(context)

    // if isAutoRenewal is false, we spawn the modal for use to enter CC details
    if (!context.isAutoRenewal) {
      setRenewModal(true)
      return
    }

    // if isAutoRenewal is true, we can just toggle to false and submit right away
    const res = await userOrderAutorenewalUpdateHandler(
      {
        orderNumber: context.orderNumber,
        orderLineNumber: context.orderLineNumber,
      },
      false,
      context,
    )
    if (res) {
      setRenewResponseModal(true)
    }
  }

  // Turn off state
  const onRenewResponseClickHandler = () => {
    setRenewResponseModal(false)
  }

  /******************************
   *  RENDER
   *******************************/
  const renderMembershipItem = ({
    membership,
    index,
    type,
  }: {
    membership: IMembership
    index: number
    type: MembershipTypes
  }) => {
    return (
      <>
        {/* Separator */}
        {index > 0 && (
          <>
            <Spacer size={20} />
            <hr
              style={{
                height: '1px',
                border: 0,
                backgroundColor: colors.gray300,
              }}
            />
            <Spacer size={20} />
          </>
        )}

        {/* Read Only / AutoRenew Fields */}
        <Grid container spacing={4}>
          {/* Membership Type */}
          <Grid xs={12} sm={6} item>
            <InputText
              readOnly
              name="ch-membership-type"
              label="Type"
              value={membership?.memberTypeLabel}
            />
          </Grid>

          {/* Membership Number */}
          <Grid xs={12} sm={6} item>
            <InputText
              readOnly
              name="ch-membership-type"
              label="Member Number"
              value={membership?.memberNumber}
            />
          </Grid>

          {/* Membership Status */}
          <Grid xs={12} sm={6} item>
            <InputText
              readOnly
              name="ch-membership-status"
              label="Status"
              value={membership?.memberStatus}
            />
          </Grid>

          {/* Membership Dates */}
          <Grid xs={12} sm={6} item>
            <InputText
              readOnly
              name="ch-membership-date"
              label="Membership Dates"
              value={dateRangeLabelFormatter(
                membership?.cycleBeginDate,
                membership?.cycleEndDate,
              )}
            />
          </Grid>

          {/* Membership Auto Renew */}
          <Grid xs={12} item>
            <Toggle
              onOff
              disabled={isMutationSubmitting}
              name="ch-membership-auto"
              label="Auto-Renew"
              labelPlacement="default"
              size="medium"
              checked={membership.autoRenew}
              onChange={() =>
                onRenewHandler({
                  membership: type,
                  typeLabel: membership.memberTypeLabel,
                  orderNumber: membership.orderNumber,
                  orderLineNumber: membership.orderLineNumber,
                  isAutoRenewal: membership?.autoRenew,
                  membershipObject: membership,
                })
              }
            />
          </Grid>
        </Grid>
      </>
    )
  }

  const currentMembershipRender = currentMembership ? (
    <Box header={{ title: 'ATD Membership Details' }}>
      {/* Current Membership */}
      <Grid container spacing={4}>
        {/* Legend */}
        <Grid xs={12} item>
          <InputSectionLabel component={'legend'}>
            Current Membership
          </InputSectionLabel>
        </Grid>
      </Grid>

      {renderMembershipItem({
        index: 0,
        membership: currentMembership,
        type: MembershipTypes.CURRENT,
      })}

      {/* Membership - Learn More */}
      <Grid container spacing={4}>
        <Grid xs={12} item>
          <Typography>
            {strings.PROFILE.MEMBERSHIP.MORE_INFO}{' '}
            <TextLink
              variant="primary"
              href={Helper.getPathFromRouteKey('MYATD_BENEFITS_CENTER')}
            >
              {strings.PROFILE.MEMBERSHIP.BENEFITS_CENTER}
            </TextLink>
            .
          </Typography>
        </Grid>
      </Grid>
    </Box>
  ) : (
    <></>
  )

  // Chapter Memberships
  const chapterMembershipRender = !!chapterMemberships?.length ? (
    <>
      <Spacer fullWidth size={24} />

      <Box header={{ title: 'Chapter Membership Details' }}>
        <Grid container spacing={4}>
          {/* Legend */}
          <Grid xs={12} item>
            {/*
             @todo should use react pluralize but need to have InputSectionLabel handle react elements
            */}
            <InputSectionLabel component={'legend'}>
              {`Current Membership${chapterMemberships?.length > 1 ? 's' : ''}`}
            </InputSectionLabel>
          </Grid>
        </Grid>

        {chapterMemberships.map((chapterMembership, index) => {
          return (
            <React.Fragment
              key={`${chapterMembership.id}_${chapterMembership.orderLineNumber}`}
            >
              {renderMembershipItem({
                index,
                membership: chapterMembership,
                type: MembershipTypes.CHAPTER,
              })}
              {/* Membership - Learn More */}
              <Grid container spacing={4}>
                <Grid xs={12} item>
                  <Typography>
                    For more information about your chapter membership, visit{' '}
                    <TextLink
                      variant="primary"
                      href={chapterMembership?.chapterURL}
                    >
                      {chapterMembership?.chapterName}
                    </TextLink>
                  </Typography>
                </Grid>
              </Grid>
            </React.Fragment>
          )
        })}
      </Box>
    </>
  ) : (
    <></>
  )

  // Future Membership
  const futureMembershipRender = futureMembership?.length ? (
    <>
      <Spacer fullWidth size={24} />
      <Box header={{ title: 'Future Membership Details' }}>
        {futureMembership.map((item, index) => {
          return (
            item && (
              <React.Fragment key={`${item.id}_${item.orderLineNumber}`}>
                {renderMembershipItem({
                  index,
                  membership: item,
                  type: MembershipTypes.FUTURE,
                })}
              </React.Fragment>
            )
          )
        })}
      </Box>
    </>
  ) : (
    <></>
  )

  // Auto-Renew Modal - Confirmation / Ended
  const modal = (
    <Modal
      footerNoSeparator
      headerNoSeparator
      open={renewResponseModal}
      size="medium"
      title="Your auto renewal has been ended."
      onClose={onRenewResponseClickHandler}
    >
      <Typography paragraph>
        Auto renewal has been discontinued for the following Membership:
      </Typography>
      <Typography paragraph>
        <strong>{membershipContext?.typeLabel}</strong>
      </Typography>
      <Typography paragraph>
        If you wish to turn auto renewal back on, you can do so at any time.
      </Typography>

      <Button
        variant="primary"
        size="medium"
        onClick={onRenewResponseClickHandler}
      >
        Okay
      </Button>
    </Modal>
  )

  return (
    <>
      {currentMembershipRender}
      {chapterMembershipRender}
      {futureMembershipRender}

      <Spacer fullWidth size={24} />

      {/* Auto-Renew Payment Form Modal */}
      <AutoRenewForm
        membershipContext={membershipContext}
        renewModal={renewModal}
        setRenewModal={setRenewModal}
        isMutationSubmitting={isMutationSubmitting}
        renewalHandler={userOrderAutorenewalUpdateHandler}
      />

      {modal}
    </>
  )
}

export default MembershipForm
